import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { CurrentInventoryButton } from "../../common/current-inventory-button";
import { SplitGroupButton } from "../../common/split-group-button";
import * as styles from "./outbound-order.module.css";

export const ItemsCard = memo(function ItemsCard({
  fulfillmentGroup,
  triggerReload,
  everythingDisabled,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  triggerReload: () => Promise<void>;
  everythingDisabled: boolean;
}) {
  const lineItemsQuantity = useMemo(() => {
    return fulfillmentGroup.items.reduce((acc, item) => acc + item.quantity, 0);
  }, [fulfillmentGroup]);

  return (
    <Flex
      bgColor="primary"
      dir="column"
      flexDirection="column"
      gap="none"
      radius="md"
      w="full"
    >
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.flatBottomCorners}
        justify="space-between"
        p="3xl"
      >
        <Flex flexDirection="column" justify="center">
          <Text color="secondary" fontSize="sm" fontWeight="medium">
            Items in this shipment ({lineItemsQuantity})
          </Text>
        </Flex>
        <Flex align="center" gap="sm">
          <CurrentInventoryButton fulfillmentGroups={[fulfillmentGroup]} />
          <SplitGroupButton
            disabled={everythingDisabled}
            fulfillmentGroup={fulfillmentGroup}
            isSlideout={false}
            size="sm"
            triggerReload={triggerReload}
          />
        </Flex>
      </Flex>
      <ItemsCardTable fulfillmentGroup={fulfillmentGroup} />
    </Flex>
  );
});

export const ItemsCardTable = memo(function ItemsCardTable({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData;
}) {
  const team = useContext(TeamContext);
  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;
  const items = useMemo(() => {
    return groupFulfillmentOrderLineItems(fulfillmentGroup.items, {
      degroupByCustomAttributes: showCustomAttributes,
    });
  }, [fulfillmentGroup, showCustomAttributes]);

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      className={styles.flatTopCorners}
      dir="column"
      gap="xs"
      height="fit-content"
      w="full"
    >
      <ItemTableHeader />
      <Flex dir="column" flex="1" gap="md" w="full">
        {items.map((item, index) => (
          <Flex
            borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
            borderColor="secondary"
            borderStyle="solid"
            gap="none"
            key={index}
            w="full"
          >
            <ProductListRow
              customAttributes={
                showCustomAttributes
                  ? hideUnderscoreCustomAttributes
                    ? item.customAttributes?.filter(
                        (attr) => !attr.key.startsWith("_"),
                      )
                    : item.customAttributes
                  : undefined
              }
              imageTooltipPlacement="top"
              imageUrl={item.imageUrl}
              price={item.unitDiscountedPrice.amount}
              priceMoney={toMoney(item.unitDiscountedPrice)}
              quantity={item.quantity}
              sku={item.sku}
              title={item.productTitle || item.title}
              variantTitle={item.variantTitle}
            />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});
